/**
 * @file routing-utils.ts
 * @description
 * -----------------------------------------------------
 * Utilidades para manejar rutas y patrones de rutas
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-11-17
 * @author Correcta
 * @author Daniel Sentamans
 * @copyright © Correcta
 * @license Private
 */

/**
 * Compara un patrón de ruta con una ruta real para verificar si coinciden.
 *
 * @param pattern - Patrón de ruta (ej: "/[locale]/bot/[botId]")
 * @param path - Ruta real (ej: "/es/bot/bot-1")
 * @returns Verdadero si coinciden, falso en caso contrario
 */
export function matchPathPattern(pattern: string, path: string): boolean {
  const pParts = pattern.split("/").filter(Boolean);
  const tParts = path.split("/").filter(Boolean);
  if (pParts.length !== tParts.length) return false;
  for (let i = 0; i < pParts.length; i++) {
    const pp = pParts[i];
    const tp = tParts[i];
    if (pp.startsWith("[") && pp.endsWith("]")) continue;
    if (pp !== tp) return false;
  }
  return true;
}

/**
 * Verifica si una ruta es navegable comparando con los patrones de rutas del backend.
 *
 * @param href - Ruta a verificar
 * @param set - Set de patrones de rutas del backend
 * @returns
 */
export function isPathNavigable(
  href: string,
  set: Set<string> | null,
): boolean {
  if (!set) {
    return href === "/[locale]" || href === "/";
  }
  // Verificar si existe exactamente esta ruta
  if (set.has(href)) return true;

  // Verificar si coincide con algún patrón dinámico
  for (const pattern of set) {
    if (matchPathPattern(pattern, href)) {
      return true;
    }
  }

  return false;
}

/**
 * Determina si el segmento actual es dinámico comparando con los patrones de rutas.
 *
 * @param segment - El segmento actual (ej: "bot-1")
 * @param index - Índice del segmento en el array de segmentos (sin locale)
 * @param allSegments - Todos los segmentos de la ruta (sin locale)
 * @param set - Set de patrones de rutas del backend
 */
export function isDynamicSegment(
  index: number,
  allSegments: string[],
  set: Set<string> | null,
): boolean {
  if (!set) return false;

  // Construir el path completo con locale para comparar con patrones
  const fullPath = `/[locale]/${allSegments.join("/")}`;

  for (const pattern of set) {
    const patternParts = pattern.split("/").filter(Boolean);
    const pathParts = fullPath.split("/").filter(Boolean);

    // Deben tener la misma longitud
    if (patternParts.length !== pathParts.length) continue;

    let matches = true;
    let isDynamicAtTargetIndex = false;

    for (let i = 0; i < patternParts.length; i++) {
      const pp = patternParts[i];
      const tp = pathParts[i];

      // Si el patrón tiene un segmento dinámico
      if (pp.startsWith("[") && pp.endsWith("]")) {
        // Ajustar el índice: i incluye [locale] (posición 0),
        // pero index es relativo a segmentos sin locale
        // Por lo tanto, i=1 corresponde a index=0, i=2 a index=1, etc.
        if (i === index + 1) {
          isDynamicAtTargetIndex = true;
        }
        // El segmento dinámico acepta cualquier valor
        continue;
      }

      // Para segmentos estáticos, deben coincidir exactamente
      if (pp !== tp) {
        matches = false;
        break;
      }
    }

    // Si encontramos un patrón que coincide y el segmento objetivo es dinámico
    if (matches && isDynamicAtTargetIndex) {
      return true;
    }
  }

  return false;
}
